import React, {useMemo} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {useParams, Link} from 'react-router-dom';
import {useMutation, useQuery} from "react-query";
import StatusBadge from "../../../components/StatusBadge";
import Loading from "../../../components/Loading";
import {apollo} from "../../../utils/apollo";
import {GET_SETTLEMENT, GET_SETTLEMENT_REPORT_URL, GET_SETTLEMENT_RECEIPT_URL} from "../../../utils/graphql";
import Button from "../../../components/Button";
import InformationLine from "../../../components/InformationLine";
import MoneyHighlight from "../../../components/MoneyHighlight";
import {useTranslation} from "react-i18next";
import {CreditCardIcon, ArrowLeftIcon} from "@heroicons/react/24/outline";

function SettlementView() {
    const {settlement_uuid} = useParams();
    const {t} = useTranslation();

    const getSettlementReportUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_REPORT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReportUrl) {
                window.location.href = data?.getSettlementReportUrl;
            }
        }
    })

    const getSettlementReceiptUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_RECEIPT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReceiptUrl) {
                window.location.href = data?.getSettlementReceiptUrl;
            }
        }
    })
    const fetchSettlement = useQuery(
        ['settlement', {settlement_uuid}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SETTLEMENT,
                variables: {
                    settlement_uuid,
                },
            });
            return data?.getSettlement;
        },
        {keepPreviousData: false}
    );
    const settlement = useMemo(() => fetchSettlement?.isSuccess ? fetchSettlement?.data : null, [fetchSettlement]);

    return (
        <DashboardLayout>
            <div>
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-2">
                        <Link to="/merchant/settlements" className="p-2.5 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-[#0b6a8c] hover:border-cyan-100 hover:bg-cyan-50/50 shadow-sm transition-all active:scale-95">
                            <ArrowLeftIcon className="h-5 w-5" />
                        </Link>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <div className="p-2 rounded-xl bg-cyan-50 text-[#0b6a8c] shadow-sm border border-cyan-100/50">
                                    <CreditCardIcon className="h-6 w-6" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{t('settlement.view_title') || 'Settlement Details'}</h2>
                            </div>
                            <p className="text-gray-500 text-sm ml-1">{t('settlement.view_description') || 'View detailed information about this settlement.'}</p>
                        </div>
                    </div>
                </div>

                {fetchSettlement.isLoading && (
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-20 text-center">
                        <Loading message={t('settlement.loading_title') || 'Loading Settlement'} fullHeight={false} />
                    </div>
                )}

                {(fetchSettlement.isSuccess && settlement) && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/30">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white text-gray-400 border border-gray-100 shadow-sm">
                                            <CreditCardIcon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">{t('settlement.settlement_details')}</h3>
                                            <p className="text-sm font-mono font-semibold text-gray-700 mt-0.5">{settlement?.settlementUuid}</p>
                                        </div>
                                    </div>
                                    <StatusBadge status={settlement?.status} className="!rounded-full px-4 py-1.5 text-[11px] font-bold shadow-sm" />
                                </div>
                            </div>
                            
                            <div className="p-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <div className="space-y-8">
                                        <div>
                                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400"></span>
                                                {t('settlement.view_sections.basic_information')}
                                            </h4>
                                            <div className="space-y-5 pl-3.5 border-l border-gray-50">
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.merchant')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1">{settlement?.merchant?.merchantName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.supplier')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1">{settlement?.supplier?.supplierName || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.user')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1">{settlement?.user?.name}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.authority')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1">{settlement?.authority?.name || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400"></span>
                                                {t('settlement.view_sections.additional_information')}
                                            </h4>
                                            <div className="space-y-5 pl-3.5 border-l border-gray-50">
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.payment_reference')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1 break-all">{settlement?.paymentReference || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-400">{t('settlement.view_sections.remarks')}</p>
                                                    <p className="text-sm font-bold text-gray-900 mt-1">{settlement?.remarks || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-8">
                                        <div className="bg-gray-50/50 rounded-3xl p-6 border border-gray-100">
                                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">{t('settlement.view_sections.financial_details')}</h4>
                                            <div className="space-y-6">
                                                <div className="bg-white p-4 rounded-[1.25rem] shadow-sm border border-gray-100">
                                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('settlement.view_sections.request_amount')}</p>
                                                    <MoneyHighlight value={settlement?.requestAmount} currency={settlement?.currency?.symbol} className="text-[#0b6a8c] font-extrabold text-2xl" />
                                                </div>
                                                <div className="bg-white p-4 rounded-[1.25rem] shadow-sm border border-gray-100">
                                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t('settlement.view_sections.settlement_amount')}</p>
                                                    {!!settlement?.settlementAmount ? (
                                                        <MoneyHighlight value={settlement?.settlementAmount} currency={settlement?.currency?.symbol} className="text-green-500 font-extrabold text-3xl" />
                                                    ) : (
                                                        <span className="text-lg font-bold text-gray-300 italic">{t('pay.pending_processing')}</span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 pt-2">
                                                    <div>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('settlement.view_sections.settlement_date')}</p>
                                                        <p className="text-sm font-bold text-gray-700 mt-1">{settlement?.settlementDate || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('settlement.view_sections.created_at')}</p>
                                                        <p className="text-sm font-bold text-gray-700 mt-1">{settlement?.createdAt}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
                                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">{t('settlement.view_sections.documents')}</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <button 
                                                    onClick={() => {
                                                        if (settlement?.receipt?.startsWith('http')) {
                                                            window.location.href = settlement.receipt;
                                                        } else {
                                                            getSettlementReceiptUrlMutation.mutate({settlementUuid: settlement?.settlementUuid});
                                                        }
                                                    }} 
                                                    disabled={!settlement?.receipt || getSettlementReceiptUrlMutation.isLoading}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gray-50 text-gray-700 font-semibold text-sm border border-gray-100 hover:bg-cyan-50 hover:text-[#0b6a8c] hover:border-cyan-100 transition-all active:scale-95 disabled:opacity-50 disabled:grayscale disabled:pointer-events-none"
                                                >
                                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    {t('settlement.download_receipt') || 'Receipt'}
                                                </button>

                                                <button 
                                                    onClick={() => getSettlementReportUrlMutation.mutate({settlementUuid: settlement?.settlementUuid})} 
                                                    disabled={getSettlementReportUrlMutation.isLoading}
                                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gray-50 text-gray-700 font-semibold text-sm border border-gray-100 hover:bg-cyan-50 hover:text-[#0b6a8c] hover:border-cyan-100 transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                                                >
                                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    {t('settlement.download_report') || 'Report'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default SettlementView;
